<template>
  <section
    v-if="isNewsPage"
    :id="id"
    class="news"
  >
    <div class="news__filter">
      <search-input
        v-model="query"
        :placeholder="i18n.searchBox"
        @keyup:enter="search"
      >
        <template #search>
          <slot name="search-title" />
        </template>
        <c-button
          use-custom-button="secondary"
          @click="search"
        >
          {{ i18n.searchButton }}
        </c-button>
      </search-input>

      <div class="news__category">
        <slot name="filter-title" />
        <ul
          class="news__list"
          @click="pushCategoryToGTM"
        >
          <category-tag
            v-for="(category, index) in _categories"
            :key="index"
            :class="{ selected: category.selected }"
            @click="toggleCategory(category)"
          >
            {{ category.categoryName }}
          </category-tag>
        </ul>
      </div>
    </div>

    <div class="news__container">
      <div v-if="state.results.length === 0">
        <slot name="no-cards-found" />
      </div>

      <div class="news__cards">
        <div
          v-for="(result, index) in state.results"
          :key="index"
          :class="{ 'news--first-card': index === 0 && current === 1 }"
        >
          <card-news
            :link="result.link"
            :image="result.image"
            :alt="result.alt"
            :news-page="true"
            :categories="result.categories"
            @click="pushGTM"
          >
            <template #title>
              {{ result.title }}
            </template>
            <template #text>
              {{ result.text }}
            </template>
            <template #category="{ category }">
              {{ category }}
            </template>
            <template #date>
              {{ result.date }}
            </template>
          </card-news>
        </div>
      </div>

      <paginator
        v-if="state.total"
        :current="current"
        :i18n="i18n"
        :total="state.total"
        :limit="limit"
        scroll-to="news"
        @paginate="handlePagination"
      />
    </div>
  </section>

  <c-ct
    v-else
    :id="id"
    :two-color-bg="true"
    :fixed-size="true"
    class="recent-news"
    :fluid="true"
  >
    <heading
      :has-button="true"
      align="left"
      title-color="#FFFFFF"
      :is-mobile-grid="true"
      class="recent-news__heading"
    >
      <template #title>
        <slot name="title" />
      </template>
      <slot name="button" />
    </heading>
    <div
      class="recent-news__cards"
      @click="pushGTM"
    >
      <slot name="news-cards" />
    </div>
  </c-ct>
</template>

<script>
import { mapState } from 'vuex'
import CardNews from '@molecules/card-news/card-news.vue'
import Heading from '@molecules/heading/heading.vue'
import CButton from '@atoms/button/button.vue'
import CCt from '@atoms/disposition/ct.vue'
import SearchInput from '@molecules/search-input/search-input.vue'
import Paginator from '@molecules/paginator/paginator.vue'
import CategoryTag from '@atoms/category-tag/category-tag.vue'
import isOutbound from '@tagging/isOutbound.js'
import API from './api'

export default {
  name: 'recent-news-wrapper',
  components: {
    CardNews,
    Heading,
    CButton,
    CCt,
    SearchInput,
    Paginator,
    CategoryTag,
  },
  props: {
    isNewsPage: {
      type: Boolean,
    },
    api: {
      type: String,
    },
    i18n: {
      type: Object,
      default() {
        return {
          paginatorPrevious: 'Previous',
          paginatorNext: 'Next',
          searchButton: 'Search',
          searchBox: 'New search',
        }
      },
    },
    limit: {
      type: Number,
      default: 4,
    },
    categories: {
      type: Array,
    },
    id: {
      type: String
    },
  },
  data() {
    return {
      selectedCategories: [],
      // eslint-disable-next-line vue/no-reserved-keys
      _categories: []
    }
  },
  computed: {
    ...mapState({
      state: (state) => state.searchNews,
    }),
    current: {
      get() {
        return this.$store.state.searchNews.current
      },
      set(current) {
        this.$store.commit('searchNews/paginate', current)
      },
    }
  },
  watch: {
    'state.numberOfResults': function (newValue) {
      if (newValue !== undefined) {
        this.pushSearchToGTM()
      }
    },
  },
  created() {
    Object.assign(API.options, {
      search: this.api,
    })

    const params = new URLSearchParams(window.location.search)
    const query = params.get('k')

    this.query = query
    const request = {
      query: this.query || '',
      categories: []
    }
    this.$store.commit('searchNews/search', request)
    // eslint-disable-next-line no-underscore-dangle
    this._categories = this.categories.map(cat => {
      return {
        categoryName: cat,
        selected: false
      }
    })
  },
  methods: {
    search() {
      if (window.history.replaceState) {
        if (this.query) {
          window.history.replaceState(
            {},
            document.title,
            `${window.location.pathname}?k=${this.query}`
          )
        }
        const request = {
          query: this.query || '',
          // eslint-disable-next-line no-underscore-dangle
          categories: this._categories
            .filter(cat => cat.selected)
            .map(cat => cat.categoryName)
        }
        this.$store.commit('searchNews/search', request)
      } else {
        window.location.search = `k=${this.query}`
      }
    },
    handlePagination(page) {
      this.current = page
    },
    toggleCategory(category) {
      category.selected = !category.selected
      this.search()
    },
    pushGTM(event) {
      const target = event.target.closest('a')

      if (!target) {
        return
      }

      const { date, cardCategories } = this.getCardInfo(target)

      if (target) {
        window.dataLayer.push({
          event: 'article_click',
          section: 'noticias',
          outbound: isOutbound(target),
          category: cardCategories,
          name: target?.innerText || '',
          date,
          filtered: this.isNewsPage ? this.checkActiveCategories() : 'vazio'
        })
      }
    },
    pushCategoryToGTM(event) {
      const target = event.target.closest('a')

      if (target) {
        window.dataLayer.push({
          event: 'tab_navigation',
          section: 'noticias',
          label: target?.innerText || ''
        })
      }
    },
    pushSearchToGTM() {
      if (!this.query || this.query.trim() === '') {
        return
      }

      window.dataLayer.push({
        event: 'search',
        section: 'noticias',
        name: this.query || '',
        quantity: this.state.numberOfResults
      })
    },
    getCardInfo(target) {
      const dateElement = target.querySelector('.card__date')
      const date = dateElement ? dateElement.innerText : ''

      const categoryElements = target.querySelectorAll('.card__category')
      const cardCategories = Array.from(categoryElements).map(el => el.innerText).join(', ')

      return { date, cardCategories }
    },
    checkActiveCategories() {
      // eslint-disable-next-line no-underscore-dangle
      return this._categories.some(cat => cat.selected)
    }
  },
}
</script>

<style scoped lang="scss">
.recent-news {
  padding: 2.75rem 2rem 3rem;

  @include screen-size("xlg") {
    padding: 3.688rem 6.5rem 3rem;
  }

  @include screen-size("xlg") {
    padding: 3.438rem 6.5rem 3rem;
  }

  &__heading {
    @include screen-size("xlg") {
      max-width: 81.5rem;
      margin: 0 auto;
    }
  }

  &__cards {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    padding-top: 4.1rem;

    @include screen-size("xlg") {
      flex-direction: row;
      justify-content: space-between;
      gap: 1rem;
      padding-top: 3.39rem;
      max-width: 81.5rem;
      margin: 0 auto;
    }
  }
}

.news {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-inline: 2rem;

  @include screen-size("xl") {
    padding-inline: unset;
  }

  &__filter {
    @include screen-size("xxl") {
      width: 100%;
    }
  }

  &__category {
    font-size: 0.875rem;
    line-height: 1rem;
    color: $neutral-03;
    margin-bottom: 5rem;
    margin-top: 1rem;

    @include screen-size("lg") {
      margin-top: 1.5rem;
      margin-bottom: 4.5rem;
    }
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding-inline-start: unset;
    margin-top: 1rem;
  }

  &__container {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 3rem;

    @include screen-size("lg") {
      gap: 6rem;
    }
  }

  &__cards {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 2.375rem;
    row-gap: 3rem;
    justify-content: center;

    @include screen-size("lg") {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      align-items: stretch;
      max-width: 81.5rem;
    }
  }

  &--first-card {
    @include screen-size("lg") {
      grid-area: 1 / 1 / 2 / 3;
    }
  }
}
</style>
